import type { BatchAction, BatchRun, BatchRuns } from '../types/batch-retry.api.types';

/** Needs Attention class a bucket maps to (Failed / Not attempted / both). */
export type NeedsAttentionClass = 'ALL' | 'FAILED' | 'NOT_ATTEMPTED';

/** Which single banner the batch shows (exactly one, or none). */
export type BatchRetryBannerKind = 'paused' | 'retrying' | 'excluded' | 'stuck' | 'not-enriched';

export interface BatchRetryState {
  /** Products still not enriched: failed (retryable + permanent) plus never attempted. */
  left: number;
  failed: number;
  /** Failed products a retry can still pick up. */
  failedRetryable: number;
  /** Failed products no retry will take (fix the source data, or exclude them). */
  failedPermanent: number;
  notAttempted: number;
  excluded: number;
  attemptsUsed: number;
  maxAttempts: number;
  attemptsLeft: number;
  paused: boolean;
  /** A retry child is enriching right now. */
  retryRunning: boolean;
  activeRun: BatchRun | null;
  has: (action: BatchAction) => boolean;
  banner: BatchRetryBannerKind | null;
}

/**
 * Products are left, the server offers no retry, and none is running: only exclusion (or
 * approving without them) moves the batch on. One rule for every "stuck" surface.
 */
export function isRetryStuck(runs: BatchRuns): boolean {
  const left =
    runs.counts.failedRetryable + runs.counts.failedPermanent + (runs.counts.notAttempted ?? 0);
  const canRetry = runs.availableActions.some((a) => a.startsWith('RETRY_'));
  return left > 0 && !canRetry && runs.activeRetryRunId === null;
}

/**
 * Derives what the retry UI shows from the runs read. Server-decided `availableActions` gate
 * the buttons; counts + attempt budget decide which single banner explains the batch.
 */
export function deriveBatchRetryState(runs: BatchRuns): BatchRetryState {
  const failedRetryable = runs.counts.failedRetryable;
  const failedPermanent = runs.counts.failedPermanent;
  const failed = failedRetryable + failedPermanent;
  const notAttempted = runs.counts.notAttempted ?? 0;
  const left = failed + notAttempted;
  const excluded = runs.counts.excluded;
  const attemptsLeft = Math.max(0, runs.maxAttempts - runs.attemptsUsed);
  const paused = runs.batchStatus === 'PAUSED';
  const retryRunning = runs.activeRetryRunId !== null && !paused;
  // While the root run (or delivery) is still moving, leftovers are not final yet.
  const settled = runs.batchStatus !== 'IN_PROGRESS' && runs.batchStatus !== 'DELIVERING';
  let banner: BatchRetryBannerKind | null = null;
  if (paused) banner = 'paused';
  else if (retryRunning) banner = 'retrying';
  else if (excluded > 0 && left === 0) banner = 'excluded';
  else if (left > 0 && settled && isRetryStuck(runs)) banner = 'stuck';
  else if (left > 0 && settled && runs.availableActions.includes('RETRY_REMAINING')) {
    banner = 'not-enriched';
  }
  const activeRun = runs.runs.find((r) => r.runId === runs.activeRetryRunId) ?? null;
  return {
    left,
    failed,
    failedRetryable,
    failedPermanent,
    notAttempted,
    excluded,
    attemptsUsed: runs.attemptsUsed,
    maxAttempts: runs.maxAttempts,
    attemptsLeft,
    paused,
    retryRunning,
    activeRun,
    has: (action) => runs.availableActions.includes(action),
    banner,
  };
}
